import React, { useState, useRef, useEffect, useMemo } from "react";
import { Spin } from "antd";

const WRAPPER_BASE_CLASS =
  "relative box-border group flex min-h-0 min-w-0 w-full max-w-full overflow-hidden bg-black/5 h-auto md:mx-auto md:max-w-[350px] md:max-lg:aspect-square md:max-lg:h-auto lg:max-w-[350px]";

const CustomVideoPlayer = ({
  src,
  className,
  wrapperClassName = "rounded-3xl",
  muted = true,
  autoPlay = true,
  loop = false,
  preload = "metadata",
  videoUploaded = false,
}) => {
  const videoRef = useRef(null);
  const loadingTimeout = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasError, setHasError] = useState(false);

  const resolvedSrc = useMemo(() => {
    if (typeof src !== "string") return src;

    const trimmedSrc = src.trim();

    if (!trimmedSrc) return "";

    if (trimmedSrc.includes("x-id=PutObject")) {
      return trimmedSrc.split("?")[0];
    }

    return trimmedSrc;
  }, [src]);

  const isImageSource = useMemo(() => {
    if (typeof resolvedSrc !== "string") return false;

    const lower = resolvedSrc.toLowerCase();

    return (
      lower.endsWith(".webp") ||
      lower.endsWith(".png") ||
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".gif") ||
      lower.endsWith(".avif") ||
      lower.startsWith("data:image/")
    );
  }, [resolvedSrc]);

  const initializeVideo = async (video) => {
    try {
      video.muted = muted;
      video.playsInline = true;
      video.autoplay = autoPlay;

      video.src = resolvedSrc;
      video.load();

      if (autoPlay) {
        try {
          await video.play();
        } catch (playError) {
          requestAnimationFrame(async () => {
            try {
              await video.play();
            } catch (retryError) {
              console.warn("Retry failed:", retryError);
            }
          });
        }
      }
    } catch (error) {
      setHasError(true);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;

      setProgress(progress);
    }
  };

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !resolvedSrc || isImageSource) {
      if (isImageSource) {
        setIsLoaded(true);
        setHasError(false);
      }

      return;
    }

    setIsLoaded(false);
    setHasError(false);

    if (loadingTimeout.current) {
      clearTimeout(loadingTimeout.current);
    }

    loadingTimeout.current = setTimeout(() => {
      if (!isLoaded && !hasError) {
        console.warn("Video loading taking longer than expected");
      }
    }, 5000);

    const events = {
      loadstart: () => {},
      loadedmetadata: () => {},
      canplay: () => {
        setIsLoaded(true);
      },
      playing: () => {
        setIsLoaded(true);
      },
      waiting: () => {},
      error: (e) => {
        setHasError(true);
      },
    };

    Object.entries(events).forEach(([event, handler]) => {
      video.addEventListener(event, handler);
    });

    video.preload = "auto";
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("x-webkit-airplay", "allow");

    initializeVideo(video);

    return () => {
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
      }

      if (video) {
        Object.entries(events).forEach(([event, handler]) => {
          video.removeEventListener(event, handler);
        });

        video.pause();
        video.src = "";
        video.load();
      }
    };
  }, [resolvedSrc, isImageSource]);

  const hasPlaybackSrc =
    typeof resolvedSrc === "string" && resolvedSrc.trim().length > 0;

  if (videoUploaded && !hasPlaybackSrc && !isImageSource) {
    return (
      <div
        className={`${WRAPPER_BASE_CLASS} lg:h-[350px] items-center justify-center ${wrapperClassName}`}
      >
        <span className="text-base font-semibold text-primary">
          Video uploaded
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${WRAPPER_BASE_CLASS} lg:h-[360px] ${wrapperClassName}`}
    >
      {videoUploaded && hasPlaybackSrc && (
        <div className="absolute top-3 right-3 z-10 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white shadow-md">
          Video uploaded
        </div>
      )}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse">
          <span className="text-gray-500">Loading video...</span>
        </div>
      )}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <span className="text-red-500">
            Error loading video. Please try refreshing.
          </span>
        </div>
      )}
     
        <video
          ref={videoRef}
          src={resolvedSrc}
          className={`${className ?? ""} h-auto w-full rounded-none object-contain md:max-lg:absolute md:max-lg:inset-0 md:max-lg:h-full md:max-lg:w-full md:max-lg:object-cover lg:absolute lg:inset-0 lg:h-full lg:w-full lg:object-cover`}
          style={{
            opacity: isLoaded ? 1 : 0,
          }}
          muted={muted}
          playsInline
          autoPlay={autoPlay}
          loop={loop}
          preload={preload}
          onTimeUpdate={handleTimeUpdate}
        >
          Your browser does not support the video tag.
        </video>
      
      {!isLoaded && !hasError && (
        <div className="absolute bottom-4 left-4 text-white text-sm bg-black/50 px-2 py-1 rounded">
          <Spin />
        </div>
      )}
    </div>
  );
};

export default CustomVideoPlayer;
